/**
 * Types for the upgraded `generate-op-note` edge function JSON response.
 */

export interface OpNoteCptCode {
  code: string;
  description: string;
  modifiers: string[];
  type?: 'primary' | 'add-on' | 'hcpcs' | 'standalone';
}

export interface OpNoteEvidencePayload {
  cpt_code: string;
  justification: string;
  documentation_reference?: string;
}

export interface OpNoteImplant {
  name: string;
  catalog_id: string;
  quantity: number;
  manufacturer?: string;
}

export interface OpNoteFinancialImpact {
  total_estimated_payout: number;
  facility_revenue: number;
  professional_revenue: number;
  modifier_22_upcharge?: number;
  summary_text: string;
}

export interface OpNoteResponse {
  optimized_note: string;
  audit_risk_score: 'LOW' | 'MEDIUM' | 'HIGH';
  clean_claim_probability: number; // 0-100
  primary_cpt: OpNoteCptCode;
  additional_cpts: OpNoteCptCode[];
  icd10_codes: string[];
  documentation_gaps: string[];
  evidence_payloads: OpNoteEvidencePayload[];
  implants_and_biologics: OpNoteImplant[];
  financial_impact: OpNoteFinancialImpact;
  warnings: string[];
  reconciliation_engine_version: string;
}

const toArray = <T>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : []);

const toCptCode = (value: any): OpNoteCptCode => {
  if (typeof value === 'string') {
    return { code: value, description: '', modifiers: [] };
  }

  return {
    code: value?.code ?? value?.cpt_code ?? '',
    description: value?.description ?? value?.descriptor ?? '',
    modifiers: toArray<string>(value?.modifiers),
    type: value?.type,
  };
};

const toRiskScore = (value: unknown): OpNoteResponse['audit_risk_score'] => {
  const normalized = String(value ?? '').toUpperCase();
  if (normalized === 'LOW' || normalized === 'MEDIUM' || normalized === 'HIGH') return normalized;
  return 'MEDIUM';
};

/**
 * Parse both upgraded and legacy response payloads into a safe render shape.
 */
export function normalizeOpNoteResponse(data: any): OpNoteResponse {
  const codingPackage = data?.coding_package ?? {};
  const revenueProtection = data?.revenue_protection ?? {};

  const primaryCpt = toCptCode(data?.primary_cpt ?? codingPackage?.primary_cpt);
  const additionalCpts = toArray<any>(data?.additional_cpts ?? codingPackage?.additional_cpts).map(toCptCode);

  const financialImpactRaw = data?.financial_impact ?? data?.revenue_impact ?? {};
  const financialImpact = typeof financialImpactRaw === 'string'
    ? {
        total_estimated_payout: 0,
        facility_revenue: 0,
        professional_revenue: 0,
        summary_text: financialImpactRaw,
      }
    : {
        total_estimated_payout: Number(financialImpactRaw?.total_estimated_payout ?? 0),
        facility_revenue: Number(financialImpactRaw?.facility_revenue ?? 0),
        professional_revenue: Number(financialImpactRaw?.professional_revenue ?? 0),
        modifier_22_upcharge: financialImpactRaw?.modifier_22_upcharge != null
          ? Number(financialImpactRaw.modifier_22_upcharge)
          : undefined,
        summary_text: financialImpactRaw?.summary_text ?? '',
      };

  const risk = toRiskScore(data?.audit_risk_score ?? revenueProtection?.audit_risk_score);
  const probability = Number(data?.clean_claim_probability ?? revenueProtection?.clean_claim_probability ?? (risk === 'LOW' ? 98 : risk === 'HIGH' ? 68 : 85));

  return {
    optimized_note: data?.optimized_note ?? data?.reconciled_note ?? data?.note ?? '',
    audit_risk_score: risk,
    clean_claim_probability: Number.isFinite(probability) ? probability : 85,
    primary_cpt: primaryCpt,
    additional_cpts: additionalCpts,
    icd10_codes: toArray<string>(data?.icd10_codes ?? codingPackage?.icd10_codes),
    documentation_gaps: toArray<string>(data?.documentation_gaps ?? revenueProtection?.documentation_gaps),
    evidence_payloads: toArray<OpNoteEvidencePayload>(data?.evidence_payloads ?? revenueProtection?.evidence_payloads),
    implants_and_biologics: toArray<OpNoteImplant>(data?.implants_and_biologics ?? data?.implants ?? codingPackage?.implants_and_biologics),
    financial_impact: financialImpact,
    warnings: toArray<string>(data?.warnings ?? revenueProtection?.warnings),
    reconciliation_engine_version: data?.reconciliation_engine_version ?? data?.engine_version ?? 'unknown',
  };
}
